import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import Dialog from '../../../common/Dialog'
import CustomSlider from '../../../common/CustomSlider'
import MaterialSelectWithDropdown from '../../../common/MaterialSelectWithDropdown'
import { Switch } from 'antd'
import { Button, Radio, Space } from 'antd'
import $t from '../../../../i18n'
import {
  ProviderLanguages,
  ProvidersTiersMap
} from '../../../../consts/ProviderConsts'
import { capitalizeFirstLetter } from '../../../../consts'
import {
  HospitalsAffiliations,
  MedicalGroupsAffiliations
} from '../../../../consts/ProvidersAffiliationConsts'

import './ProviderFilterDialog.css'

// ProviderFilterDialog is a modal that allows the user to filter the provider search results
export const ProviderFilterDialog = ({
  isOpen,
  onClose,
  onFilter,
  defaultFilterBy
}) => {
  const state = useSelector(
    state => state.providers?.providersSearchLocation?.state
  )
  // distanceFilterDefinitions from App config
  const minDistanceRadius = useSelector(
    state => state.providers?.distanceFilterDefinition?.min
  )

  const maxDistanceRadius = useSelector(
    state => state.providers?.distanceFilterDefinition?.max
  )

  const defaultDistanceRadius = useSelector(
    state => state.providers?.distanceFilterDefinition?.default
  )

  // filterBy is an object with the following properties:
  // (language, gender, providerType, tier, distanceRadius)
  // defaultFilterBy is the default state of the filterBy object
  const [filterBy, setFilterBy] = useState(defaultFilterBy)

  useEffect(() => {
    // set the filterBy state to the defaultFilterBy prop
    setFilterBy(defaultFilterBy)
  }, [defaultFilterBy])

  const marks = {
    [minDistanceRadius]: {
      label: (
        <div className='slider-edge-text left'>
          {minDistanceRadius}
          <br />
          mile
        </div>
      )
    },
    [maxDistanceRadius]: {
      label: (
        <div className='slider-edge-text right'>
          {maxDistanceRadius}
          <br />
          miles
        </div>
      )
    }
  }

  // languageList must be array of value. label object for the Select component
  const languageList = Object.values(ProviderLanguages)?.map(lang => ({
    value: lang,
    label: capitalizeFirstLetter($t('pages.language.' + lang))
  }))

  // Affiliations are still hardcoded per state, so a state that is configured but
  // has no entry here yields an empty list rather than breaking the dialog
  const affiliationOptions = affiliationsByState => {
    return Object.values(affiliationsByState?.[state] || {}).map(
      affiliation => ({
        value: affiliation,
        label: capitalizeFirstLetter(affiliation)
      })
    )
  }

  const hospitalAffiliationsList = () =>
    affiliationOptions(HospitalsAffiliations)

  const medicalGroupAffiliationsList = () =>
    affiliationOptions(MedicalGroupsAffiliations)

  // tierList must be array of value. label object for the Select component
  const tierList = Object.entries(ProvidersTiersMap)?.map(
    ([tierKey, tierVal]) => ({
      value: tierVal,
      label: capitalizeFirstLetter(
        $t('providerPage.searchResults.filter.' + tierKey)
      )
    })
  )

  // reset filterBy to empty state
  const onReset = () => {
    const clearFilterBy = {
      language: null,
      gender: null,
      tier: null,
      providerType: null,
      distanceRadius: defaultDistanceRadius || 50
    }
    setFilterBy(clearFilterBy)
    onFilter(clearFilterBy)
    onClose()
  }

  // update filterBy object with new value
  const updateFilter = (fieldName, value) => {
    setFilterBy({ ...filterBy, [fieldName]: value })
  }

  // update radio group filter - if same value is selected, unselect it
  // We use the update event on each radio button since Radio group onChange is not fired when selecting the same value
  const updateRadioGroupFilter = (fieldName, value) => {
    const currentValue = filterBy[fieldName]
    const newValue = currentValue === value ? null : value
    setFilterBy({ ...filterBy, [fieldName]: newValue })
  }

  // apply the filter and close the modal
  const onSubmit = () => {
    onFilter(filterBy)
    onClose()
  }

  return (
    <Dialog
      className='search-provider-filter-dialog'
      closable={true}
      title={$t('providerPage.searchResults.filter.title')}
      isVisible={isOpen}
      onCancel={onClose}
      footerActions={[
        <Button
          key='select'
          className='dialog-action trackable'
          onClick={onSubmit}
          data-name='apply filter'
          type='primary'
        >
          {$t('common.apply')}
        </Button>,
        <Button
          key='reset'
          className='dialog-action trackable'
          data-name='clear filter'
          onClick={onReset}
        >
          {$t('common.clear')}
        </Button>
      ]}
    >
      <div className='provider-filter-dialog-content'>
        <div className='provider-section'>
          <div className='provider-section provider-filter-dropdowns'>
            <MaterialSelectWithDropdown
              value={filterBy?.language}
              allowClear={true}
              defaultActiveFirstOption={false}
              label={$t('providerPage.searchResults.filter.language')}
              onChange={value => updateFilter('language', value)}
              data={languageList}
            />
            <MaterialSelectWithDropdown
              value={filterBy?.tier}
              allowClear={true}
              defaultActiveFirstOption={false}
              label={$t('providerPage.searchResults.filter.tier')}
              onChange={value => updateFilter('tier', value)}
              data={tierList}
            />
          </div>
          <div className='provider-section provider-filter-row'>
            <div className='provider-filter-radios'>
              <div className='provider-filter-radio-group'>
                <div className='provider-filter-title semi-bold'>
                  {$t('providerPage.searchResults.filter.gender')}:
                </div>
                <Radio.Group name='gender' value={filterBy?.gender}>
                  <Space direction='vertical'>
                    <Radio
                      value='female'
                      name='gender'
                      onClick={e =>
                        updateRadioGroupFilter('gender', e?.target?.value)
                      }
                    >
                      {$t('providerPage.searchResults.filter.female')}
                    </Radio>
                    <Radio
                      value='male'
                      name='gender'
                      onClick={e =>
                        updateRadioGroupFilter('gender', e?.target?.value)
                      }
                    >
                      {$t('providerPage.searchResults.filter.male')}
                    </Radio>
                  </Space>
                </Radio.Group>
              </div>
              <div className='provider-filter-radio-group'>
                <div className='provider-filter-title semi-bold'>
                  {$t('providerPage.searchResults.filter.providerType')}:
                </div>
                <Radio.Group name='providerType' value={filterBy?.providerType}>
                  <Space direction='vertical'>
                    <Radio
                      value='individual'
                      name='providerType'
                      onClick={e =>
                        updateRadioGroupFilter('providerType', e?.target?.value)
                      }
                    >
                      {$t('providerPage.searchResults.filter.individual')}
                    </Radio>
                    <Radio
                      value='facility'
                      name='providerType'
                      onClick={e =>
                        updateRadioGroupFilter('providerType', e?.target?.value)
                      }
                    >
                      {$t('providerPage.searchResults.filter.facility')}
                    </Radio>
                  </Space>
                </Radio.Group>
              </div>
            </div>
          </div>
          <div className='provider-filter-row'>
            <div className='provider-filter-title above-slider semi-bold'>
              {$t('providerPage.searchResults.filter.distance_radius')}:
            </div>
            <CustomSlider
              value={filterBy?.distanceRadius}
              min={minDistanceRadius}
              max={maxDistanceRadius}
              marks={marks}
              hideMarks={true}
              onChange={value => updateFilter('distanceRadius', value)}
              displayTooltip={true}
              tooltip={{
                open: true,
                placement: 'top',
                getPopupContainer: triggerNode => triggerNode.parentNode
              }}
            />
          </div>
          <div className='provider-section provider-filter-dropdowns'>
            <MaterialSelectWithDropdown
              value={filterBy?.hospitalAffiliations}
              allowClear={true}
              defaultActiveFirstOption={false}
              label={$t(
                'providerPage.searchResults.filter.hospitalAffiliations'
              )}
              onChange={value => updateFilter('hospitalAffiliations', value)}
              data={hospitalAffiliationsList()}
              fixedWidth='150px'
            />
            <MaterialSelectWithDropdown
              value={filterBy?.medicalGroupAffiliations}
              allowClear={true}
              defaultActiveFirstOption={false}
              label={$t(
                'providerPage.searchResults.filter.medicalGroupAffiliations'
              )}
              onChange={value =>
                updateFilter('medicalGroupAffiliations', value)
              }
              data={medicalGroupAffiliationsList()}
            />
          </div>
          <div className='provider-filter-row'>
            <div className='provider-filter-full-width-row-content'>
              <div className='provider-filter-title semi-bold'>
                {$t('providerPage.searchResults.filter.acceptingNewPatients')}:
              </div>
              <Switch
                checked={filterBy?.acceptingNewPatients}
                onChange={value =>
                  updateFilter('acceptingNewPatients', value ? true : null)
                }
              />
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  )
}

export default ProviderFilterDialog
